import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Element } from '../../../../common/html/element'
import { Loading } from '../../../../common/loading'
import { Tooltip } from '../../../../common/tooltip'
import { formatVal, valToPercent } from '../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../utils/common'
import { selectDefaultIndustry } from '../store/slice'
import classes from './index.module.scss'
import {
  selectHighlightItems,
  selectIndustryHighlight,
  selectLoadingHighlightItems,
  selectLoadingIndustryHighlight,
  selectRiskScaleInterpretation,
} from './store/slice'
import {
  financialRatioHighlightThunk,
  getIndustryMedianRiskScoreThunk,
  getRiskScaleInterpretationThunk,
} from './store/thunk'

const ENUM_HIGHLIGHT = {
  GROWTH: 'RT0156',
  PROFITABILITY: 'RT2002',
  LEVERAGE: 'RT0027',
  EFFICIENCY: 'RT2003',
  LIQUIDITY: 'RT0020',
}

const HighLight = () => {
  const dispatch = useDispatch()
  const vsicDefault = useSelector(selectDefaultIndustry)
  const industryHighlight = useSelector(selectIndustryHighlight)
  const highlightItems = useSelector(selectHighlightItems)
  const dataIndustryRiskScoreRange = useSelector(selectRiskScaleInterpretation)
  const loadingIndustryHighlight = useSelector(selectLoadingIndustryHighlight)
  const loadingHighlightItems = useSelector(selectLoadingHighlightItems)
  const locale = useSelector((state) => state.i18n.locale)

  const gradient = useMemo(() => {
    const maxIndustryRiskScore = industryHighlight?.maxScore || 1000
    const minIndustryRiskScore = industryHighlight?.minScore || 0
    if (!dataIndustryRiskScoreRange?.length) return ''
    const total = maxIndustryRiskScore - minIndustryRiskScore
    const stops = [...dataIndustryRiskScoreRange].reverse().map((d) => {
      const start = ((d.scoreRange_Min - minIndustryRiskScore) / total) * 100
      const end = ((d.scoreRange_Max - minIndustryRiskScore) / total) * 100
      return `${d.scoreRangeCode} ${start.toFixed(2)}%, ${
        d.scoreRangeCode
      } ${end.toFixed(2)}%`
    })
    return `linear-gradient(to right, ${stops.join(', ')})`
  }, [dataIndustryRiskScoreRange, industryHighlight])

  const highlights = useMemo(() => {
    const getItem = (type) => {
      const item = highlightItems?.[type]
      return {
        scoreValue: item?.scoreValue ? item?.scoreValue * 100 : EMPTY_VALUE,
        minValue: item?.minValue * 100 || 0,
        maxValue: item?.maxValue * 100 || 0,
        formatPercent: true,
      }
    }

    const findColor = (value) => {
      return (
        dataIndustryRiskScoreRange.find(
          (r) => value >= r.scoreRange_Min && value <= r.scoreRange_Max,
        )?.scoreRangeCode || '#0553A5'
      )
    }

    return [
      {
        title: 'companyAnalysis.industryAnalysis.INDUSTRY_MEDIAN_RISK_SCORE',
        value: Number.isInteger(industryHighlight?.medianScore)
          ? industryHighlight?.medianScore
          : formatVal(industryHighlight?.medianScore, locale),
        minValue:
          industryHighlight?.minScore !== undefined
            ? industryHighlight?.minScore
            : EMPTY_VALUE,
        maxValue:
          industryHighlight?.maxScore !== undefined
            ? industryHighlight?.maxScore
            : EMPTY_VALUE,
        colorScore:
          industryHighlight?.medianScore !== undefined
            ? findColor(Number(industryHighlight?.medianScore))
            : '#CCC',
        isScore: true,
      },
      {
        title: 'companyAnalysis.industryAnalysis.NET_REVENUE_GROWTH',
        ...getItem(ENUM_HIGHLIGHT.GROWTH),
      },
      {
        title: 'companyAnalysis.industryAnalysis.NET_PROFIT_MARGIN',
        ...getItem(ENUM_HIGHLIGHT.PROFITABILITY),
      },
      {
        title: 'companyAnalysis.industryAnalysis.ROA',
        ...getItem(ENUM_HIGHLIGHT.EFFICIENCY),
      },
      {
        title: 'companyAnalysis.industryAnalysis.TOTAL_DEBT_TO_TOTAL_EQUITY',
        ...getItem(ENUM_HIGHLIGHT.LEVERAGE),
      },
      {
        title: 'companyAnalysis.industryAnalysis.CURRENT_RATIO',
        ...getItem(ENUM_HIGHLIGHT.LIQUIDITY),
      },
    ]
  }, [industryHighlight, highlightItems, dataIndustryRiskScoreRange])

  useEffect(() => {
    dispatch(getRiskScaleInterpretationThunk())
  }, [dispatch])

  useEffect(() => {
    if (!vsicDefault) return
    dispatch(getIndustryMedianRiskScoreThunk({ VSICId: vsicDefault }))
    dispatch(financialRatioHighlightThunk({ VSICId: vsicDefault }))
  }, [vsicDefault, dispatch])

  const isLoading = loadingIndustryHighlight || loadingHighlightItems

  return (
    highlights.length > 0 && (
      <Element
        className={classes.container}
        style={{ borderBottomColor: '#e5e5e8' }}
      >
        {isLoading && <Loading />}
        <Element
          className={classes.highlightList}
          style={{ opacity: isLoading ? 0 : 1 }}
        >
          {highlights.map((item, i) => (
            <HighLightItem
              key={item.title}
              item={item}
              gradient={
                i === 0
                  ? gradient
                  : 'linear-gradient(to right, #c8e3ff 0%, #c8e3ff 100%)'
              }
            />
          ))}
        </Element>
      </Element>
    )
  )
}

export default HighLight

const HighLightItem = React.memo(({ item, gradient }) => {
  const actualValue = item.isScore ? item.value : item.scoreValue
  const hideThumb = actualValue === undefined || actualValue === EMPTY_VALUE
  const locale = useSelector((state) => state.i18n.locale)

  return (
    <div className={classes.highlightWrap}>
      <Element
        className={classes.title}
        style={{ color: '#3a414b', fontSize: 14 }}
      >
        <Translate value={item.title} />
      </Element>
      <Element
        className={classes.value}
        style={{ color: '#fff', backgroundColor: item.colorScore || '#0553A5' }}
      >
        {item.isScore ? (
          <Tooltip
            isI18n
            tooltipContent="companyAnalysis.industryAnalysis.MEDIAN"
          >
            <span>{item.value}</span>
          </Tooltip>
        ) : (
          <span>{valToPercent(item.scoreValue, locale, true)}</span>
        )}
      </Element>
      <div className={classes.rangeContainer}>
        <Tooltip
          isI18n
          tooltipContent={
            item.isScore
              ? 'companyAnalysis.industryAnalysis.MIN_MAX'
              : 'companyAnalysis.industryAnalysis.PERCENTILE_VALUE'
          }
        >
          <div className={classes.sliderContainer}>
            <input
              type="range"
              min={item.minValue}
              max={item.maxValue}
              value={item.value ?? item.scoreValue}
              className={`${classes.rangeSlider} ${
                hideThumb ? classes.noThumb : ''
              }`}
              style={{ background: gradient }}
              readOnly
            />
          </div>
        </Tooltip>

        <div className={classes.rangeLabels}>
          <Element>
            {item.formatPercent
              ? valToPercent(item.minValue, locale, true)
              : item.minValue}
          </Element>
          <Element>
            {item.formatPercent
              ? valToPercent(item.maxValue, locale, true)
              : item.maxValue}
          </Element>
        </div>
      </div>
    </div>
  )
})
